import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

// 쿠키 설정 가져오기
const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 인증 컨텍스트 생성
const AuthContext = createContext();

// 인증 컨텍스트 제공자 컴포넌트
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isTokenError, setIsTokenError] = useState(false);
  const [userInfo, setUserInfo] = useState({
    userId: null,
    createdAt: null,
    residentName: "",
    phone: "",
    apartmentBlock: "",
    apartmentUnit: "",
    isSmsConsent: false,
    isTenant: false,
    apartmentName: "",
    apartmentLawyerName: "",
    apartmentLawyerProfile: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 쿠키에서 데이터 불러오기
  // const loadFromCookies = () => {
  //   const savedAccessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  //   const savedRefreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);
  //   const savedUserInfo = Cookies.get(COOKIE_KEYS.USER_INFO);

  //   if (savedAccessToken) {
  //     setAccessToken(savedAccessToken);
  //   }

  //   if (savedRefreshToken) {
  //     setRefreshToken(savedRefreshToken);
  //   }

  //   if (savedUserInfo) {
  //     try {
  //       setUserInfo(JSON.parse(savedUserInfo));
  //       setIsAuthenticated(true);
  //     } catch (error) {
  //       console.error("쿠키에서 사용자 정보 파싱 중 오류 발생:", error);
  //     }
  //   }
  // };

  // 쿠키에 데이터 저장
  const saveAccessTokenToCookie = (token) => {
    if (token) {
      Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, token, {
        expires: COOKIE_EXPIRES.ACCESS_TOKEN,
        ...COOKIE_OPTIONS,
      });
      setAccessToken(token);
    }
  };

  const saveRefreshTokenToCookie = (token) => {
    if (token) {
      Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, token, {
        expires: COOKIE_EXPIRES.REFRESH_TOKEN,
        ...COOKIE_OPTIONS,
      });
      setRefreshToken(token);
    }
  };

  const saveUserInfoToCookie = (info) => {
    if (info) {
      Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(info), {
        expires: COOKIE_EXPIRES.USER_INFO,
        ...COOKIE_OPTIONS,
      });
      setUserInfo(info);
    }
  };

  // URL에서 토큰 추출 함수
  const getTokensFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const accessToken = searchParams.get("accessToken");
    const refreshToken = searchParams.get("refreshToken");
    const jointlyowned = searchParams.get("jointlyowned");

    // accessToken이 없으면 /not-found로 리다이렉트
    // if (!accessToken) {
    //   navigate("/not-found");
    //   return { accessToken: null, refreshToken: null, jointlyowned: null };
    // }

    return { accessToken, refreshToken, jointlyowned };
  };

  // 토큰 갱신 함수
  const refreshAccessToken = async () => {
    if (!refreshToken) {
      setIsTokenError(true);
      return null;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.REFRESH_TOKEN, {
        refreshToken,
      });

      if (response.data && response.data.success) {
        const newAccessToken = response.data.data.accessToken;
        saveAccessTokenToCookie(newAccessToken);
        setIsTokenError(false);
        return newAccessToken;
      }
      setIsTokenError(true);
      return null;
    } catch (error) {
      setIsTokenError(true);
      return null;
    }
  };

  // 초기화 함수 - URL에서 토큰을 가져와 설정
  const initialize = () => {
    try {
      // 1. URL에서 토큰 가져오기
      const {
        accessToken: urlAccessToken,
        refreshToken: urlRefreshToken,
        jointlyowned,
      } = getTokensFromUrl();

      // 2. 이미 쿠키에 저장된 토큰 확인
      const cookieAccessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      const cookieRefreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

      // 3. URL에 토큰이 있으면 쿠키에 저장
      if (urlAccessToken) {
        // 직접 쿠키에 저장 (saveAccessTokenToCookie 함수를 사용하지 않고)

        Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, urlAccessToken, {
          expires: COOKIE_EXPIRES.ACCESS_TOKEN,
          ...COOKIE_OPTIONS,
        });
        setAccessToken(urlAccessToken);

        // 리프레시 토큰 저장
        if (urlRefreshToken) {
          Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, urlRefreshToken, {
            expires: COOKIE_EXPIRES.REFRESH_TOKEN,
            ...COOKIE_OPTIONS,
          });
          setRefreshToken(urlRefreshToken);
        }

        // 저장 확인
        const savedAccessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
        const savedRefreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

        // jointlyowned 파라미터가 있으면 공동소유자 상태 저장
        if (jointlyowned === "true") {
          sessionStorage.setItem("jointlyowned", "true");
        }

        return urlAccessToken;
      }
      // 4. URL에 토큰이 없지만 쿠키에 토큰이 있으면 쿠키의 토큰 사용
      else if (cookieAccessToken) {
        setAccessToken(cookieAccessToken);

        if (cookieRefreshToken) {
          setRefreshToken(cookieRefreshToken);
        }

        return cookieAccessToken;
      }

      navigate("/not-found");
      setIsTokenError(true);
      return false;
    } catch (error) {
      navigate("/not-found");
      setIsTokenError(true);
      return false;
    }
  };

  // 사용자 정보 조회 함수
  const fetchUserInfo = async (tokenValue) => {
    if (!tokenValue) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      let token = tokenValue;
      let response;
      let retryCount = 0;

      const fetchData = async () => {
        try {
          response = await axios.get(API_ENDPOINTS.USER_INFO, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: 10000, // 10초 타임아웃 설정
          });
          setIsTokenError(false);
          return true;
        } catch (error) {
          if (retryCount === 0) {
            retryCount++;
            return false;
          }
          throw error;
        }
      };

      // 첫 번째 시도
      let isSuccess = await fetchData();

      // 실패했을 경우 한 번만 더 시도
      if (!isSuccess) {
        await new Promise((resolve) => setTimeout(resolve, 2000)); // 2초 대기
        isSuccess = await fetchData();
      }

      // 응답 구조에 따라 userInfo 설정 방식 조정
      if (response && response.data) {
        // API 응답 데이터를 그대로 사용
        const userData = response.data;

        // 쿠키에 직접 저장 (saveUserInfoToCookie 함수 대신)
        Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(userData), {
          expires: COOKIE_EXPIRES.USER_INFO,
          ...COOKIE_OPTIONS,
        });

        // 저장 확인
        const savedUserInfo = Cookies.get(COOKIE_KEYS.USER_INFO);

        // 상태 업데이트
        setUserInfo(userData);
        setIsAuthenticated(true);

        // 토큰 쿠키 다시 확인 (디버깅용)
        const checkAccessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
        const checkRefreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

        // 액세스 토큰이 없으면 다시 저장 시도
        if (!checkAccessToken && token) {
          Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, token, {
            expires: COOKIE_EXPIRES.ACCESS_TOKEN,
            ...COOKIE_OPTIONS,
          });
        }
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("accessToken");
        cleanUrl.searchParams.delete("refreshToken");
        // window.history.replaceState(
        //   {},
        //   document.title,
        //   cleanUrl.pathname + cleanUrl.search
        // );
      } else {
        setIsTokenError(true);
      }
    } catch (error) {
      setIsTokenError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 로그아웃 함수
  // const logout = () => {
  //   // 상태 초기화
  //   setAccessToken(null);
  //   setRefreshToken(null);
  //   setIsAuthenticated(false);
  //   setIsTokenError(false);

  //   // 기본값으로 설정
  //   setUserInfo({
  //     userId: null,
  //     createdAt: null,
  //     residentName: "",
  //     phone: "",
  //     apartmentBlock: "",
  //     apartmentUnit: "",
  //     isSmsConsent: false,
  //     isTenant: false,
  //     apartmentName: "",
  //     apartmentLawyerName: "",
  //     apartmentLawyerProfile: "",
  //   });

  //   // 쿠키 삭제
  //   Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN, { ...COOKIE_OPTIONS });
  //   Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN, { ...COOKIE_OPTIONS });
  //   Cookies.remove(COOKIE_KEYS.USER_INFO, { ...COOKIE_OPTIONS });
  // };

  // 컴포넌트 마운트 시 초기화 및 사용자 정보 조회
  // useEffect(() => {
  //   // URL에서 토큰 확인
  //   const token = initialize();

  //   // 토큰이 있으면 사용자 정보 조회
  //   if (token) {
  //     fetchUserInfo(token);
  //   } else {
  //     setIsTokenError(true);
  //     setIsLoading(false);
  //   }
  // }, []);

  // 제공할 값들
  const value = {
    accessToken,
    refreshToken,
    userInfo,
    isLoading,
    isAuthenticated,
    isTokenError,
    fetchUserInfo,
    refreshAccessToken,
    // logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 인증 컨텍스트 사용을 위한 훅
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth는 AuthProvider 내부에서만 사용할 수 있습니다");
  }
  return context;
};
